import React, { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquareText } from "lucide-react";
import { api } from "../lib/api";
import { ModuleHeader } from "../components/intel/IntelKit";
import ExpertLocked from "../components/expert/ExpertLocked";
import ExpertRequestModal from "../components/expert/ExpertRequestModal";
import ExpertEngagementCard from "../components/expert/ExpertEngagementCard";

const ACTIVE_STATUSES = ["new", "under_review", "assigned", "in_progress"];

export default function ExpertPage() {
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    const accessRes = await api.expertAccess();
    if (!accessRes.ok) { setLoading(false); return; }
    setAccess(accessRes.data);
    if (accessRes.data.access === "full") {
      const reqRes = await api.expertRequests();
      if (reqRes.ok) setRequests(reqRes.data.requests || []);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upsertRequest = (updated) => {
    if (!updated) return;
    setRequests((prev) => {
      const exists = prev.some((r) => r.id === updated.id);
      return exists ? prev.map((r) => (r.id === updated.id ? updated : r)) : [updated, ...prev];
    });
  };

  const active = requests.find((r) => ACTIVE_STATUSES.includes(r.status));
  const history = requests.filter((r) => !ACTIVE_STATUSES.includes(r.status));

  return (
    <div className="p-6 md:p-8 space-y-7 max-w-4xl" data-testid="expert-page">
      <ModuleHeader overline="Partners & Tools" title="C-ORE Expert" />
      <p className="text-[14.5px] text-slate-500 -mt-2 max-w-xl">
        A human perspective from the C-ORE team when a business decision needs deeper thought.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : access?.access !== "full" ? (
        <ExpertLocked reason={access?.locked_reason || "pulse"} />
      ) : active ? (
        <ExpertEngagementCard request={active} onUpdated={upsertRequest} />
      ) : (
        <div data-testid="expert-empty-state" className="max-w-2xl border border-slate-200 rounded-2xl bg-white p-8 md:p-10">
          <div className="w-11 h-11 rounded-full bg-[#F0F5FF] flex items-center justify-center">
            <MessageSquareText className="w-5 h-5 text-[#1E3A5F]" />
          </div>
          <h2 className="mt-5 text-[17px] font-bold text-[#0F1B2D]">What are you working through?</h2>
          <p className="mt-2 text-[13.5px] text-slate-500 leading-relaxed max-w-xl">
            Share a decision, challenge, opportunity, or business problem you're currently thinking
            about. The C-ORE team will review your context and get back to you.
          </p>
          <button
            data-testid="expert-open-request-btn"
            onClick={() => setShowModal(true)}
            className="mt-6 rounded-full bg-[#1E3A5F] text-white px-6 py-3 text-[13px] font-semibold hover:opacity-90 transition"
          >
            Talk to C-ORE Expert
          </button>
        </div>
      )}

      {access?.access === "full" && history.length > 0 && (
        <div data-testid="expert-history-section" className="max-w-2xl space-y-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Past engagements</div>
          {history.map((r) => (
            <div key={r.id} data-testid={`expert-history-${r.id}`} className="rounded-xl border border-slate-100 px-4 py-3">
              <div className="text-[13px] text-[#0F1B2D] line-clamp-2">{r.request_text}</div>
              <div className="text-[11.5px] text-slate-400 mt-1">{r.category} · Completed</div>
            </div>
          ))}
        </div>
      )}

      <ExpertRequestModal open={showModal} onClose={() => setShowModal(false)}
        onCreated={(req) => { setShowModal(false); upsertRequest(req); }} />
    </div>
  );
}
